import React, { useEffect, useMemo, useState } from 'react'
import Select, { type SingleValue, type StylesConfig } from 'react-select'
import { useNotificationContext } from '../../hooks/useNotificationContext'
import type { ListNotificationsParams, NotificationListStatus } from '../../helper/notifcationsHelper'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { hydroReactSelectStyles } from '@/lib/hydroReactSelectStyles'

type StatusFilterValue = 'all' | NotificationListStatus
type RangeFilterValue = 'all' | '7d' | '30d'

type FilterOption = { value: string; label: string }

const filterSelectStyles = hydroReactSelectStyles as StylesConfig<FilterOption, false>

const STATUS_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'open', label: 'Needs attention (open)' },
  { value: 'active', label: 'Active only' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'resolved', label: 'Resolved' },
]

const RANGE_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All time' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
]

const menuPortalTarget = typeof document !== 'undefined' ? document.documentElement : undefined

function daysAgoIso(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

function buildListParams(status: StatusFilterValue, range: RangeFilterValue): ListNotificationsParams {
  const params: ListNotificationsParams = { limit: 100, page: 1 }
  if (status !== 'all') params.status = status
  if (range === '7d') params.since = daysAgoIso(7)
  if (range === '30d') params.since = daysAgoIso(30)
  return params
}

const Notifications = () => {
  const {
    notifications,
    listTotal,
    loading,
    error,
    acknowledgeById,
    resolveById,
    refreshNotifications,
  } = useNotificationContext()

  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('active')
  const [rangeFilter, setRangeFilter] = useState<RangeFilterValue>('7d')

  const listParams = useMemo(
    () => buildListParams(statusFilter, rangeFilter),
    [statusFilter, rangeFilter]
  )

  useEffect(() => {
    void refreshNotifications(listParams)
  }, [listParams, refreshNotifications])

  const statusSelectValue = useMemo(
    () => STATUS_OPTIONS.find((o) => o.value === statusFilter) ?? STATUS_OPTIONS[0],
    [statusFilter]
  )
  const rangeSelectValue = useMemo(
    () => RANGE_OPTIONS.find((o) => o.value === rangeFilter) ?? RANGE_OPTIONS[0],
    [rangeFilter]
  )

  return (
    <div className="mx-auto w-full max-w-5xl px-3 pb-12 pt-4 sm:px-5">
      <Card className="border-zinc-700 bg-zinc-950/75 text-zinc-100 shadow-lg ring-1 ring-zinc-800/60">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <CardTitle className="text-base text-zinc-50">Alerts</CardTitle>
            <CardDescription className="text-zinc-400">
              Filter by status and time. The nav badge uses active alerts only (including legacy rows
              without a status).
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            className="h-10 shrink-0 border-zinc-600 bg-zinc-900 text-zinc-100 hover:bg-zinc-800 sm:self-start"
            onClick={() => void refreshNotifications()}
          >
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor="alerts-status-filter" className="text-xs font-medium text-zinc-400">
                Status
              </Label>
              <div className="relative z-10 min-h-[44px] w-full min-w-0">
                <Select<FilterOption, false>
                  instanceId="alerts-status"
                  inputId="alerts-status-filter"
                  classNamePrefix="alerts-status"
                  className="w-full min-w-0"
                  options={STATUS_OPTIONS}
                  value={statusSelectValue}
                  onChange={(opt: SingleValue<FilterOption>) => {
                    if (!opt) return
                    setStatusFilter(opt.value as StatusFilterValue)
                  }}
                  styles={filterSelectStyles}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  isSearchable={false}
                  blurInputOnSelect
                  aria-label="Filter by status"
                />
              </div>
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <Label htmlFor="alerts-range-filter" className="text-xs font-medium text-zinc-400">
                Time range
              </Label>
              <div className="relative z-10 min-h-[44px] w-full min-w-0">
                <Select<FilterOption, false>
                  instanceId="alerts-range"
                  inputId="alerts-range-filter"
                  classNamePrefix="alerts-range"
                  className="w-full min-w-0"
                  options={RANGE_OPTIONS}
                  value={rangeSelectValue}
                  onChange={(opt: SingleValue<FilterOption>) => {
                    if (!opt) return
                    setRangeFilter(opt.value as RangeFilterValue)
                  }}
                  styles={filterSelectStyles}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition="fixed"
                  menuPlacement="auto"
                  isSearchable={false}
                  blurInputOnSelect
                  aria-label="Filter by time range"
                />
              </div>
            </div>
          </div>

          <p className="text-xs text-zinc-500">
            Showing {notifications.length} of {listTotal} matching alert{listTotal === 1 ? '' : 's'} (up to 100 per
            load).
          </p>

          {loading ? <p className="text-sm text-zinc-400">Loading notifications...</p> : null}
          {error ? <p className="text-sm text-red-300">{error}</p> : null}
          {!loading && notifications.length === 0 ? (
            <p className="text-sm text-zinc-400">No notifications match these filters.</p>
          ) : null}
          {notifications.map((n) => (
            <div
              key={n._id}
              className="rounded-lg border border-zinc-800 bg-zinc-900/70 px-3 py-3 sm:px-4"
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-1">
                  <p className="text-sm font-medium text-zinc-100">{n.message}</p>
                  <p className="text-xs text-zinc-500">
                    {[n.type ?? '—', n.severity ?? '—', n.status ?? 'active'].join(' · ')}
                  </p>
                  {n.updatedAt ? (
                    <p className="text-xs text-zinc-600">
                      Updated {new Date(n.updatedAt).toLocaleString()}
                    </p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {!n.status || n.status === 'active' ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      className="h-8 bg-zinc-700 text-zinc-100 hover:bg-zinc-600"
                      onClick={() => void acknowledgeById(n._id)}
                    >
                      Acknowledge
                    </Button>
                  ) : null}
                  {n.status !== 'resolved' ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 border-zinc-600 bg-zinc-900 text-zinc-100 hover:bg-zinc-800"
                      onClick={() => void resolveById(n._id)}
                    >
                      Resolve
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  )
}

export default Notifications
